// CheckoutInterceptor.jsx
import * as React from "react";
import { useSearchParams, useLocation, useNavigate } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";

/**
 * Listens for Stripe-style query params on the current URL and shows a
 * simple success/canceled message. No backend verification.
 *
 * Usage: place <CheckoutInterceptor/> somewhere inside your /dashboard page.
 */
export default function CheckoutInterceptor({
  autoHideDuration = 6000,
  clearParams = true, // remove params from the URL after showing the message
}) {
  const [open, setOpen] = React.useState(false);
  const [variant, setVariant] = React.useState("info");
  const [message, setMessage] = React.useState("");

  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const success = searchParams.get("success") === "true";
    const canceled = searchParams.get("canceled") === "true";
    const sessionId = searchParams.get("session_id");

    if (success) {
      setVariant("success");
      setMessage(
        sessionId
          ? "Payment successful. Your subscription is active."
          : "Payment successful."
      );
      setOpen(true);
      if (clearParams) stripQueryParams();
      return;
    }

    if (canceled) {
      setVariant("warning");
      setMessage("Payment was canceled. No charges were made.");
      setOpen(true);
      if (clearParams) stripQueryParams();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const stripQueryParams = () => {
    // Replace current URL with same path, no query params
    navigate(location.pathname, { replace: true });
  };

  return (
    <Snackbar
      open={open}
      onClose={() => setOpen(false)}
      autoHideDuration={autoHideDuration}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        onClose={() => setOpen(false)}
        severity={variant}
        variant="filled"
        sx={{ width: "100%" }}
      >
        {message}
      </Alert>
    </Snackbar>
  );
}